'use strict';

import _, {each, extend, values, contains, find} from 'underscore';

import appStore from '@uw-it-sis/lib-react/lib/AppStore';

import Bitmap from '@uw-it-sis/lib-react/lib/Bitmap';
import FacetModel from './FacetModel';

const analyticsStore = appStore.getAnalyticsStore();
import {MetricKeys, MetricLabels} from './Constants';

/**
 * Facets store.
 *
 * This class has to instantiate.
 *
 * @param facetConfigs A facet configuration JSON object.
 *
 * Notes about facet configs:
 *
 *  o 'component' is optional. The default is 'checkbox'
 *  o 'analyticsKey' is optional. If you don't specify one the facet Id will be used to construct a key.
 *
    let facetConfigs = {
        idPropertyName: "id",
        facets: [
            {
                id: "genEdRequirements",
                label: "Gen Ed Requirements",
                component: "button",
                analyticsKey: "Gen-Ed",
                valueFinderMethod: function (course) {
                    let genEds = course.genEduReqs;
                    if (genEds.length == 0) {
                        return ("");
                    }
                    //  Format the gened codes in a predictable way and add delimiters.
                    return GenEdUtils.makeGenEdString(genEds, GenEdUtils.DELIMITER.WORDS);
                },
                filterSortMethod: function (a, b) {
                    // sort magic goes here. Return -1, 0, 1
                    return 0;
                }
            },
            {
                id: "online",
                label: "Online Learning",
                analyticsKey: "Search-Keyword-Online-Facet-Interaction",
                valueFinderMethod: function (course) {
                    if (course.onlineLearningCodes.length) {
                        let online = [];
                        course.onlineLearningCodes.map(function (code) {
                            online.push(code.split(":")[0]);
                        });
                        return online;
                    } else {
                        return "None";
                    }
                }
            }
        ]
    };
 *
 *
 */
class FacetsStore {
    constructor(facetConfigs) {

        this._facetModels = facetConfigs.facets.map(function (facet, index) {
            return(
                new FacetModel(facet.id, facet.label, facet.valueFinderMethod, facet.filterSortMethod,
                    facet.component, facet.analyticsKey, facet.helpContent, facet.cssClasses)
            );
        });

        this._idPropertyName = facetConfigs.idPropertyName ? facetConfigs.idPropertyName : "id",

        /*
         * Map where the key is the entity id and the value is a Bitmap of the filters across all facets which
         * match (1) or don't (0) the entity.
         *
         * To see the binary value of a filter bitmap go ... bm.toString() ... where is decimal value of the bitmap.
         */
       this._entityToFilterBitmapsMap = {};

        /*
         * A map where the key is an entity id and the value is an array of facet ids where the entity matches only a
         * single filter within a facet.
         */
        this._entityToSingleValuedFacetsMap = {};

        /*
         * A map where the key is an entity id and the value is an array of facet ids where the entity matches more than
         * one filter within a facet.
         */
        this._entityToMultiValuedFacetsMap = {};

        /*
         * A map of result items for quick access during filtering.
         *
         * The key is the entity id, the value is the entity object.
         */
        this._entityMap = {};

        /**
         * A map where the key is a facet id and the value is a bitmap of the filters that are contained within
         * the facet.
         */
        this._filterGroupBitmapsMap = {};

        /*
         * A sorted array of filter ids. This determines the offsets of the filters in the bitmaps.
         */
        this._sortedFilterIds = [];
    }


    /**
     * Clobbers any existing filters and then creates new ones, sets total counts in the filters, and builds
     * the filter bitmaps which are used during filtering.
     *
     * Note: Filter match counts are populated as the filters are created, so no need to run filter afterwards.
     */
    buildFilters(entities) {
        /*
         * Pass each facet the collection of result set items one at a time. facet.process() will return
         * a list of filters that match the item. This list will be added to a map like ...
         *
         * Note: Filter ids are in the format "facetId"__"filterValue" and this map doesn't follow that format.
         *
         * {'courseId0': {'filterId0': null, 'filterId1': null, ...},
         *  'courseId1': {'filterId1': null, 'filterId2': null, ...}}
         *
         * After all items are processed this map will be used to create a map of Bitmaps where the value
         * of each filter is represented. The order of the filters in the bitmaps stored in the map is determined
         * by creating an array of all filters and sorting them alphabetically. So, given this set of (sorted)
         * filters ...
         *
         *      (F1__1, F1__2, F2__a, F2__b)
         *
         * ... and these results ...
         *
         *      [{id: "c1", field1: "1", field2: "b"},
         *       {id: "c2", field1: "2", field2: "a"}]
         *
         * First a map that looks like this would be created.
         *
         *    {"c1": {"F1__1": null, "F2__b": null},
         *     "c2": {"F1__2": null, "F2__a": null}}
         *
         * From the map above, these bitmaps would be created ...
         *
         *      {"c1": "1001",
         *       "c2": "0110"}
         *
         * This map basically defines which filters match or do not match a particular entity. Or put a slightly
         * different way ... the bitmap for each entity maps the entity to one or more values for each facet.
         *
         */
        // let startTime = new Date();
        this.removeAllFilters();
        let entityFilterMap = {};
        each(entities, function(entity) {
            let entityId = entity[this._idPropertyName];
            this._entityMap[entityId] = entity;
            each(this._facetModels, function(facet) {
                let filterMap = entityFilterMap[entityId];
                if (! filterMap) {
                    filterMap = {};
                    entityFilterMap[entityId] = filterMap;
                }
                let filterIds = facet.process(entity);
                //  Add the filter ids into the entity filter map
                extend(filterMap, filterIds, entityId);
            }, this);
        }, this);

        /*
         * Create a sorted array of filter ids.
         */
        this._sortedFilterIds = [];
        each(this._facetModels, function(facet) {
             this._sortedFilterIds = this._sortedFilterIds.concat(facet.getFilterIds());
        }, this);

        /*
         * Sort the filter ids in alphabetical order. The index of the ids in the array will determine offsets of
         * the filters in the bitmaps.
         */
        this._sortedFilterIds.sort();

        /*
         * Build the entity / matching filters bitmaps
         */
        this._entityToFilterBitmapsMap = {};
        each(entities, function(entity) {
            //  Initialize the bitmap to zero(s)
            let bitmap = new Bitmap();

            //  Grab the filter ids associated with the entity from the entityFilterMap
            let filterIds = entityFilterMap[entity[this._idPropertyName]];
            //  Get the offset of the filter id and set the bit high in the bitmap.
            for (let filterId in filterIds) {
                bitmap.setBit(this._getFilterIndex(filterId), true);
            }
            this._entityToFilterBitmapsMap[entity[this._idPropertyName]] = bitmap;
        }, this, entityFilterMap);

        /*
         * Build the filter group bitmaps
         */
        each(this._facetModels, function(facet) {
            let bitmap = new Bitmap();
            let filterModels = facet.getFilterModels();
            each(filterModels, function(filter) {
                let i = this._getFilterIndex(filter.getId())
                bitmap.setBit(i, true);
            }, this, bitmap);
            this._filterGroupBitmapsMap[facet.getId()] = bitmap;
        }, this);

        /*
         * Build the single-valued facet id map.
         */
        each(entities, function(entity) {
            let bitmap = new Bitmap();
            let entityId = entity[this._idPropertyName];
            let entityBm = this._entityToFilterBitmapsMap[entityId];
            this._entityToSingleValuedFacetsMap[entityId] = [];
            this._entityToMultiValuedFacetsMap[entityId] = [];
            /*
             * Determine if there is more than one matching filter for a particular facet
             * by looping through the filter group bitmap (which is basically a mask for all filters within a
             * particular facet). And the entity bitmap (aka the filters that the entity matches) with the
             * facet group bitmap. Then check to see if a single bit is set. Then push the id to the
             * corresponding collection.
             *
             * Doing this up front is more efficient than doing it while matching.
             */
            for (let facetId in this._filterGroupBitmapsMap) {
                let filterGroupBitmap = this._filterGroupBitmapsMap[facetId];
                let entityFilterGroupBits = Bitmap.and(entityBm, filterGroupBitmap);
                if (entityFilterGroupBits.isSingleBitSet()) {
                    this._entityToSingleValuedFacetsMap[entityId].push(facetId);
                } else {
                    this._entityToMultiValuedFacetsMap[entityId].push(facetId);
                }
            }
        }, this);

        // time difference in ms
        // console.log("EL buildFilters(): " + (new Date() - startTime));
    }

    /**
     * Filter the given entities and set match counts.
     * Returns a collection of entities that matched the current filtering state.
     */
    filter() {
        // let startTime = new Date();

        let matches = {};

        this.resetAllFilterCounts();

        /*
         * Create a bitmap representation of the current state of the filters (taking into consideration
         * the effect of the "allFiltersActive" flag on the facet). This will serve as a template for
         * the permutations that we need to count matches later.
         */
        let currentFilterStatesBitmap = new Bitmap();
        each(this._facetModels, function(facet) {
            let filterModels = facet.getFilterModels();
            each(filterModels, function(filter) {
                let i = this._getFilterIndex(filter.getId());
                //  If the allFiltersActive flag is on in the facet then turn all of the filters on.
                if (facet.isAllFiltersActive()) {
                    currentFilterStatesBitmap.setBit(i, true);
                } else {
                    //  Otherwise, only need to turn the bit if it's on in the filter.
                    if (filter.isSelected()) {
                        currentFilterStatesBitmap.setBit(i, true);
                    }
                }
            }, this, facet, currentFilterStatesBitmap);
        }, this, currentFilterStatesBitmap);

        /*
         * For each filter make a bitmap where it is on but it siblings are off like this ...
         *      Start with the currentFilterStatesBitmap value
         *      Turn the entire set of filters off for the facet
         *      Create a bitmap for each filter where it is turned on but its siblings are off
         */
        let siblingsOffBitmaps = {};
        each(this._facetModels, function(facet) {
            //  Create a bitmap where all of the filters for this facet are off
            let allOff = new Bitmap(currentFilterStatesBitmap.getValue());
            each(facet.getFilterIds(), function(filterId) {
                //  Switch off the bit for filterId. Note: The bitmap has to be written from right to left.
                let i = this._getFilterIndex(filterId);
                allOff.setBit(i, false);
            }, this, allOff);

            //  Create a bitmap for each filters where it is on and its siblings are off.
            let individualsOn = {};
            each(facet.getFilterIds(), function(filterId) {
                //  Switch on the bit for filterId.
                individualsOn[filterId] = new Bitmap(allOff.getValue());
                let i = this._getFilterIndex(filterId);
                individualsOn[filterId].setBit(i, true);
            }, this, allOff, individualsOn);

            //  Add the individuals to the complete list.
            extend(siblingsOffBitmaps, individualsOn);
        }, this, siblingsOffBitmaps);

        /*
         * Now loop through the filters and set the counts by comparing the entity/filter bitmap to the filter
         * bitmaps.
         */
        each(this._facetModels, function(facet) {
            let filterModels = facet.getFilterModels();
            each(filterModels, function(filter) {
                let filterBm = siblingsOffBitmaps[filter.getId()];
                /*
                 * Evaluate each entity against the "siblings off" bitmap to get the counts.
                 * If the entity has no multi-valued properties then the comparison is just a simple
                 * AND / XOR. Otherwise, a more complex comparison is required.
                 *
                 * Get the entity Id and bitmap for which filters the entity matches.
                 */
                for(let entityId in this._entityToFilterBitmapsMap) {
                    let entityBm = this._entityToFilterBitmapsMap[entityId];
                    /*
                     * First try a regular old AND/XOR match (it's a sunk cost since we'll need the xor bitmap
                     * anyway if the first compare fails).
                     *
                     * "and" the bitmaps to clear any bits that are not relevant.
                     *
                     * So, for instance if the filters that match the entity are 011100 and the current filter states
                     * are 101101 then 101101 & 011100 == 001100
                     */
                    let andBm = Bitmap.and(entityBm, filterBm);
                    //  "xor" the "and" result and the entityBm.
                    let xorBm = Bitmap.xor(andBm, entityBm);
                    /*
                     * If no bits are still high in the xor bitmap then this a match. Update the counts and stuff.
                     * If this is a fail, then do a more complex match.
                     */
                    if (xorBm.isZero()) {
                        //  Increment the counter
                        filter.incrementMatchCount();
                        //  Add to the result set if the filter is active
                        if (facet.isAllFiltersActive() || filter.isSelected()) {
                            matches[entityId] = this._entityMap[entityId];
                        }
                    } else {
                        let isMatch = true;
                        /*
                         * Since a simple match failed, loop through the single-value facets for this entity and
                         * see if one of those failed. If any of them fail, then this entity isn't a match and no
                         * more processing is required.
                         */
                        let facetIds = this._entityToSingleValuedFacetsMap[entityId];
                        for (let x = 0; x < facetIds.length; x++) {
                            let facetId = facetIds[x];
                            let filterGroupBitmap = this._filterGroupBitmapsMap[facetId];
                            let aBm = Bitmap.and(xorBm, filterGroupBitmap);
                            if (! aBm.isZero()) {
                                isMatch = false;
                                break;
                            }
                        }

                        /*
                         * If the single-valued filter groups all checked out then look at the multi-valued ones.
                         * Process each facet (aka filter group) until a fail is found
                         */
                        if (isMatch)  {
                            // Get the list of multi-valued facets for this entity.
                            facetIds = this._entityToMultiValuedFacetsMap[entityId];

                            let foundFacet = false;

                            // Loops through them and see if the entity matches for the facet being examined
                            for (let x = 0; x < facetIds.length; x++) {
                                let facetId = facetIds[x];

                                if (facetId === facet.id) {
                                    foundFacet = true;
                                    let filterGroupBitmap = this._filterGroupBitmapsMap[facetId];
                                    /*
                                     * Determine if it matches ...
                                     * Take the entityFilterGroup bits and "and" them with the filterBm. This gets rid
                                     * of the irrelevant bits.
                                     * Then and those bits with the filter bitmap (aka the current state of the filters)
                                     * If what's left over is _not_ zero then it is a match. Otherwise, it not a match
                                     * and processing can cease.
                                     */
                                    let entityFilterGroupBits = Bitmap.and(entityBm, filterGroupBitmap);
                                    let matchingBits = Bitmap.and(entityFilterGroupBits, filterBm);
                                    if (matchingBits.isZero()) {
                                        isMatch = false;
                                        break;
                                    }
                                }
                            }

                            // if there is no multi value map for this facet for this entity,
                            // the entity doesn't match this facet, and should not be considered a match
                            if (! foundFacet) {
                                isMatch = false;
                            }
                        }

                        if (isMatch) {
                            //  Increment counts ...
                            filter.incrementMatchCount();
                            //  Add to the result set if the filter is active
                            if (facet.isAllFiltersActive() || filter.isSelected()) {
                                matches[entityId] = this._entityMap[entityId];
                            }
                        }
                    }
                }
            }, this, facet, siblingsOffBitmaps);
        }, this, siblingsOffBitmaps);

        // time difference in ms
        // console.log("EL filter(): " + (new Date() - startTime));
        return(values(matches));
    }

    /**
     * Removes all of the filters for each facet.
     */
    removeAllFilters() {
        each(this._facetModels, function(facet) {
            facet.removeFilters();
            facet.setAllFiltersActive(true);
        });
    }

    /**
     * Sets current and projected filter counts to zero.
     */
    resetAllFilterCounts() {
        each(this._facetModels, function(facet) {
            facet.resetFilterCounts();
        });
    }

    /*
     * Handle a filter toggle for a binary component (e.g. checkbox or button).
     */
    _toggleFacetFilter(filterId) {
        let tokens = filterId.split("__");
        let facetId = tokens[0];

        let facet = this._getFacetById(facetId);
        // First turn off the Facet's allFiltersActive flag if it is on.
        if (facet.isAllFiltersActive()) {
            facet.setAllFiltersActive(false);
        }

        //  Toggle the filter.
        let filter = facet.getFilter(filterId);
        if (filter.isSelected()) {
            filter.setSelected(false);
        } else {
            filter.setSelected(true);
        }

        //  Get all of the "on" filters. If there aren't any then turn the "allFiltersActive" flag on.
        let onFilters = _.filter(facet.getFilterModels(), function(filter) {
            if (filter.isSelected()) {
                return(filter);
            }
        });

        if (onFilters.length == 0) {
            facet.setAllFiltersActive(true);
        }

        this._doFilterChangeAnalytics(facet, filterId, filter.selected);
    }

    /**
     * Returns facet and filter models to their original states.
     *
     * @param facetId The Id of the facet to be cleared.
     */
    clearFacet(facetId) {
        this.setAllFiltersActive(true, facetId);
        // Analytics
        analyticsStore.addEvent("Facet-Clear", {}, {facetId: facetId});
    }

    /**
     * Sets the flag to ignore individual filter states. Also, turns all filters on or off.
     * @param facetId The id of the facet. If null then all filters will get reset.
     * @param state true/on or false/off
     */
    setAllFiltersActive(state, facetId) {
        if (facetId) {
            let facet = this._getFacetById(facetId);
            facet.setAllFiltersActive(state);
        } else {
             for (let x = 0, xLen = this._facetModels.length; x < xLen; x++) {
                let facet = this._facetModels[x];
                facet.setAllFiltersActive(state);
             }
        }
    }

    /**
     * Updates filters for the given filter ids. If filterIds is an array then the change is for a
     * non-boolean component (e.g. the time slider). Otherwise, it's for a binary component.
     * @param filterIds
     */
    changeFilters(filterIds) {
        let booleanWidget = true;
        if (Object.prototype.toString.call(filterIds) === '[object Array]') {
            booleanWidget = false;
        }

        if (booleanWidget) {
            this._toggleFacetFilter(filterIds);
        } else {
            this._setFiltersOn(filterIds);
        }
    }

    /**
     * Turns the given list of filterIds on and turns all of the others off.
     * @param filterIds An array of filterIds.
     */
    _setFiltersOn(filterIds) {
        let filterId = filterIds[0];
        let tokens = filterId.split("__");
        let facetId = tokens[0];

        let facet = this._getFacetById(facetId);

        /*
         * If the number of filterIds matches the number of filters then set the all filters flag to true and be
         * done. Note, this also turns all of the filter isSelected flags to false.
         */
        if (filterIds.length === facet.getFilterModels().length) {
            facet.setAllFiltersActive(true, facetId);
            return;
        }

        // First turn off the Facet's allFiltersActive flag. (This leaves the filter isSelected flags alone)
        facet.setAllFiltersActive(false, facetId);

        /*
         * Iterate through the filters turning off filters which aren't in the list of filter ids.
         * and turning on filters which are in the list.
         */
        facet.getFilterModels().map(function (filter, index) {
            let filterId = filter.getId();

            // Considered being a little more sophisticated here, but decided against it ;)
            if (contains(filterIds, filterId)) {
               filter.setSelected(true);
            } else {
               filter.setSelected(false);
            }
        });

        this._doFilterChangeAnalytics(facet, filterIds, true);
    }

    /**
     * Emits analytics events.
     *
     * @param facet The facet which was changed.
     * @param filterIds The filter Id or Ids within the facet that were changed. Can be a string or an array.
     * @private
     */
    _doFilterChangeAnalytics(facet, filterIds, selected) {
        // See if the facet has an analytics key defined. If not, make one up.
        let facetAnalyticsKey = facet.getAnalyticsKey();
        if (! facetAnalyticsKey) {
            facetAnalyticsKey = facet.getId();
        }

        let idString = filterIds;
        // Delimit array elements with a pipe.
        if (Object.prototype.toString.call(filterIds) === '[object Array]') {
            idString =  filterIds.join("|");
        }

        const labels = analyticsStore.label(MetricLabels.facetName, facetAnalyticsKey).get();
        analyticsStore.addEvent(MetricKeys.facetInteraction, labels,
            {facetValue: idString, newValue: selected});
    }

    getFacetModels() {
        return(this._facetModels);
    }

    /**
     * This function uses to get all active filters from the facet models.
     * @returns A map of Facet name To Values Selected
     */
    getActiveFacetFilterModels() {
        let facetMap = {};
        each(this._facetModels, function (facet) {
            if (!facet.allFiltersActive) {
                let filters = [];
                each(facet.filterModels, function (filter) {
                    if (filter.selected) {
                        filters.push(filter.key);
                    }
                });
                facetMap[facet.id] = filters;
            }
        }, this);
        return facetMap;
    }

    /**
     * This function is used to set the filters to be selected, based on the given active filters.
     * @param activeFacets This is a Map which contains the facet names to facet values selected.
     *                     e.g. Object should be like this : {"credentialAdmissionType":["Undefined"]}
     */
    setActiveFacetFilterModels(activeFacets) {
        if(activeFacets){
            each(this._facetModels, function (facet) {
                let filters = activeFacets[facet.id];
                if(filters){
                    each(facet.filterModels, function (filter) {
                        if(filters.includes(filter.key)){
                            filter.selected = true;
                            //If filter is selected then the allFiltersActive on facet becomes false.
                            facet.allFiltersActive = false;
                        }
                    });
                }
            }, this);
        }
    }

    /*
     * Gets a facet model by its id.
     */
    _getFacetById(facetId) {
        return(
            find(this._facetModels,
                function(facet) {
                    return(facet.id == facetId);
                }
            )
        );
    }

    /**
     * Returns true if the given entity matches only a single filter within all facets.
     */
    _matchesOnlySingleValues(entityId) {
        return (this._entityToMultiValuedFacetsMap[entityId].length === 0);
    }

    /*
     * Gets the index of a filter in the filter bitmap.
     */
    _getFilterIndex(filterId) {
        return (this._sortedFilterIds.length - this._sortedFilterIds.indexOf(filterId) - 1);
    }
}

export default FacetsStore;